import React, { useState } from 'react';
import Seo from '@/components/Seo';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Logo } from '@/components/Logo';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, ShieldCheck, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * DataDeletionRequest
 * -------------------
 * Public intake form for COPPA / CCPA / GDPR-style data deletion requests.
 *
 * What we collect:
 *   - Email of the account whose data should be deleted
 *   - Optional full name (helps admin triage)
 *   - Optional list of children to delete (if parent only wants specific
 *     children removed, not the whole account)
 *   - Optional reason / context
 *
 * What we do NOT do here:
 *   - We never auto-delete. The endpoint just files the request. An admin
 *     verifies the requester owns the email (e.g. via a reply to a
 *     verification email) before actually purging the data, since coach,
 *     parent, waiver, and Stripe records are intertwined.
 */
// Phase 117 — this form now doubles as the "Your Privacy Choices" page
// (CCPA/CPRA "Do Not Sell or Share" + access/correction requests), so the
// footer link is honest. Same intake endpoint; the type just routes triage.
const REQUEST_TYPES = [
    { value: 'delete', label: 'Delete my data (account or specific children)' },
    { value: 'access', label: 'Send me a copy of my data' },
    { value: 'correct', label: 'Correct information about me or my child' },
    { value: 'do_not_sell', label: 'Record a "Do Not Sell or Share" preference' },
    { value: 'other', label: 'Something else' },
];

export default function DataDeletionRequest() {
    const [requestType, setRequestType] = useState('delete');
    const [email, setEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const [studentNamesRaw, setStudentNamesRaw] = useState('');
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null); // { request_id, message }

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            toast.error('Please enter the email on your account.');
            return;
        }
        setSubmitting(true);
        try {
            const studentNames = studentNamesRaw
                .split(/[,\n]/)
                .map((s) => s.trim())
                .filter(Boolean);
            const r = await api.post('/legal/deletion-request', {
                request_type: requestType,
                email: email.trim(),
                full_name: fullName.trim() || undefined,
                student_names: studentNames.length ? studentNames : undefined,
                reason: reason.trim() || undefined,
            });
            setSubmitted(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not submit your request. Please email support@splashpad.pro instead.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#f4f7fb]" data-testid="deletion-request-page">
            <Seo
                title="Your Privacy Choices | SplashPad"
                description="Request deletion, a copy, or a correction of your personal data, or record a Do Not Sell or Share preference, in line with our privacy policy."
                canonical="https://splashpad.pro/privacy/delete-my-data"
                noIndex
            />
            <div className="flex items-center justify-between px-4 py-4 sm:px-6">
                <Link to="/"><Logo /></Link>
                <Button asChild variant="outline" className="h-9 rounded-xl">
                    <Link to="/privacy"><ArrowLeft className="mr-1 h-3.5 w-3.5" /> Privacy Policy</Link>
                </Button>
            </div>

            <div className="mx-auto max-w-xl px-4 pb-16 sm:px-6">
                <Card className="sd-card p-6 sm:p-10">
                    <div className="mb-5 flex items-center gap-3">
                        <div className="rounded-xl bg-[#00d4ff]/15 p-2.5">
                            <ShieldCheck className="h-5 w-5 text-[#0a1628]" />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl font-bold text-[#0a1628]" data-testid="privacy-choices-title">Your privacy choices</h1>
                            <p className="mt-0.5 text-sm text-[#1a3a5c]/80">
                                Delete your data, get a copy of it, fix something, or record a "Do Not Sell or Share" preference. We never sell your information — this form just makes it official.
                            </p>
                        </div>
                    </div>

                    {submitted ? (
                        <div
                            className="rounded-xl border border-emerald-500/30 bg-emerald-500/8 p-6 text-center"
                            data-testid="deletion-request-success"
                        >
                            <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-600" />
                            <h2 className="font-display text-lg font-bold text-[#0a1628]">Request received</h2>
                            <p className="mx-auto mt-2 max-w-sm text-sm text-[#1a3a5c]">
                                {submitted.message || 'We will email you within 30 days to confirm next steps.'}
                            </p>
                            <p className="mt-3 text-xs text-[#1a3a5c]/70">
                                Reference ID: <code className="font-mono">{submitted.request_id}</code>
                            </p>
                            <div className="mt-5 flex justify-center">
                                <Button asChild variant="outline" className="h-9 rounded-xl">
                                    <Link to="/">Back home</Link>
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4" data-testid="deletion-request-form">
                            <div>
                                <Label htmlFor="request_type">What would you like to do?</Label>
                                <Select value={requestType} onValueChange={setRequestType}>
                                    <SelectTrigger id="request_type" className="mt-1 h-11 rounded-xl" data-testid="privacy-request-type">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {REQUEST_TYPES.map((t) => (
                                            <SelectItem key={t.value} value={t.value} data-testid={`privacy-request-type-${t.value}`}>{t.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="email">Account email <span className="text-[#ff6b35]">*</span></Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    placeholder="parent@example.com"
                                    className="mt-1 h-11 rounded-xl"
                                    data-testid="deletion-request-email"
                                />
                                <p className="mt-1 text-xs text-[#1a3a5c]/70">
                                    The email on your SplashPad account. We'll reach out from <strong>support@splashpad.pro</strong> to verify it's you before acting on anything.
                                </p>
                            </div>

                            <div>
                                <Label htmlFor="full_name">Your name (optional)</Label>
                                <Input
                                    id="full_name"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    placeholder="Jane Parent"
                                    className="mt-1 h-11 rounded-xl"
                                    data-testid="deletion-request-name"
                                />
                            </div>

                            {requestType === 'delete' && (
                            <div>
                                <Label htmlFor="children">Specific children to remove (optional)</Label>
                                <Textarea
                                    id="children"
                                    value={studentNamesRaw}
                                    onChange={(e) => setStudentNamesRaw(e.target.value)}
                                    placeholder="Sammy Swimmer, Lila Swimmer"
                                    rows={2}
                                    className="mt-1 rounded-xl"
                                    data-testid="deletion-request-children"
                                />
                                <p className="mt-1 text-xs text-[#1a3a5c]/70">
                                    Leave blank to remove the whole account, or list specific children (comma-separated) to remove just them.
                                </p>
                            </div>
                            )}

                            <div>
                                <Label htmlFor="reason">Anything else we should know? (optional)</Label>
                                <Textarea
                                    id="reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={3}
                                    className="mt-1 rounded-xl"
                                    data-testid="deletion-request-reason"
                                />
                            </div>

                            <Button
                                type="submit"
                                disabled={submitting}
                                className="sd-cta sd-focus h-11 w-full rounded-xl"
                                data-testid="deletion-request-submit"
                            >
                                {submitting ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>
                                ) : (
                                    <>{requestType === 'delete' ? 'Submit deletion request' : 'Submit request'}</>
                                )}
                            </Button>

                            <p className="text-xs text-[#1a3a5c]/70">
                                Once verified, we typically complete deletion within 30 days. Some records (signed waivers, payment receipts) may be retained as required by law.
                                Prefer email? Write to <a className="underline" href="mailto:support@splashpad.pro">support@splashpad.pro</a>.
                            </p>
                        </form>
                    )}
                </Card>

                <div className="mt-4 flex justify-center gap-4 text-xs text-[#1a3a5c]/70">
                    <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
                    <span aria-hidden>·</span>
                    <Link to="/terms" className="hover:underline">Terms of Service</Link>
                </div>
            </div>
        </div>
    );
}
